import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ReleaseState, Cover, Tag, message } from '@common/musicHubCommon';
import { getArtistMusic, deleteMusic } from '../../../services/index';
import { getArtistID, getUserData, useGenreStore } from '../../../store';
import './index.scss';

// 格式化日期为 dd-mm-yyyy 格式
function formatDateDDMMYYYY(tsSeconds?: number) {
  if (!tsSeconds) return '';
  try {
  const d = new Date(tsSeconds * 1000);
  const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = String(d.getFullYear()); // 四位年份
    return `${dd}-${mm}-${yyyy}`;
  } catch (error) {
    console.error('Failed to format date:', error);
    return '';
  }
}

// 删除确认对话框组件 - 按照设计稿样式
interface DeleteConfirmProps {
  visible: boolean;
  title: string;
  type: 'song' | 'album';
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmDialog: React.FC<DeleteConfirmProps> = ({ visible, title, type, onConfirm, onCancel }) => {
  if (!visible) return null;
  
  return (
    <div className="delete-dialog-overlay">
      {/* Backdrop */}
      <div className="delete-dialog-backdrop" onClick={onCancel} />
      
      {/* Dialog */}
      <div className="delete-dialog">
        {/* Content */}
        <div className="delete-dialog__content">
          <h3 className="delete-dialog__title">Delete "{title}"?</h3>
          <p className="delete-dialog__desc">
            This {type} will be permanently removed and cannot be undone.
          </p>
        </div>
        
        {/* Buttons */}
        <div className="delete-dialog__buttons">
          <button className="delete-dialog__btn delete-dialog__btn--confirm" onClick={onConfirm}>
            Delete
          </button>
          <button className="delete-dialog__btn delete-dialog__btn--cancel" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );  
};

// 歌曲类型定义
interface SongItem {
  id: string;
  title: string;
  artist: string;
  cover?: string;
  album: string;
  genre: string[];
  releaseDate: string;
  status: 'published' | 'review' | 'rejected';
}

// 表格行组件（桌面端）
interface SongRowProps {
  song: SongItem;
  onDelete: (id: string) => void;
  getGenreLabel: (id: string) => string;
}

const SongRow: React.FC<SongRowProps> = ({ song, onDelete, getGenreLabel }) => (
  <tr className="song-row">
    <td className="song-row__cell song-row__cell--song">
      <div className="song-info">
        <Cover src={song.cover} size="small" />
        <span className="song-info__title">{song.title}</span>
      </div>
    </td>
    <td className="song-row__cell">{song.album}</td>
    <td className="song-row__cell">
      <div className="tags">
        {song.genre.map(g => (
          <Tag key={g}>{getGenreLabel(g)}</Tag>
        ))}
      </div>
    </td>
    <td className="song-row__cell">{song.releaseDate}</td>
    <td className="song-row__cell">
      <ReleaseState state={song.status} />
    </td>
    <td className="song-row__cell song-row__cell--actions">
      <div className="song-row__actions-wrap">
        <button className="action-btn action-btn--danger" onClick={() => onDelete(song.id)}>
          <img src="https://akoss.bangcdn.net/fe/Delete.svg" alt="Delete" width={24} height={24} />
        </button>
      </div>
    </td>
  </tr>
);

// 移动端歌曲卡片组件
interface MobileSongCardProps {
  song: SongItem;
  onDelete: (id: string) => void;
  getGenreLabel: (id: string) => string;
  isLast?: boolean; // 是否是最后一个卡片
}

const MobileSongCard: React.FC<MobileSongCardProps> = ({ song, onDelete, getGenreLabel, isLast }) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);
  
  return (
    <div className="mobile-song-card">
      <Cover src={song.cover} size="medium" />
      <div className="mobile-song-card__info">
        <span className="mobile-song-card__title">{song.title}</span>
        <div className="mobile-song-card__tags">
          {song.genre.map(g => (
            <Tag key={g}>{getGenreLabel(g)}</Tag>
          ))}
        </div>
        <span className="mobile-song-card__date">Release: {song.releaseDate}</span>
        <div className="mobile-song-card__status-row" ref={menuRef}>
          <ReleaseState state={song.status} />
          <button className="mobile-song-card__menu-btn" onClick={() => setShowMenu(!showMenu)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
          </button>
          {showMenu && (
            <div className={`mobile-song-card__dropdown ${isLast ? 'mobile-song-card__dropdown--up' : ''}`}>
              <button onClick={() => { onDelete(song.id); setShowMenu(false); }}>
                <img src="https://akoss.bangcdn.net/fe/Delete.svg" alt="" width={20} height={20} />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MySongs: React.FC = () => {
  const [songs, setSongs] = useState<SongItem[]>([]);
  const [deleteModal, setDeleteModal] = useState<{ visible: boolean; id: string; title: string }>({
    visible: false,
    id: '',
    title: '',
  });
  
  // 从 store 获取 genre 映射
  const genreOptions = useGenreStore((state) => state.genreOptions);
  
  // genre ID 转 label 的映射函数
  const getGenreLabel = useCallback((id: string) => {
    const option = genreOptions.find(g => g.value === id);
    return option?.label || id;
  }, [genreOptions]);

  useEffect(() => {
    const artistID = getArtistID();
    if (!artistID) {
      console.warn('No artistID found, user may not be logged in');
      return;
    }
    
    getArtistMusic({
      artistID,
    }).then((res: any) => {
      console.log(res);
      // 转换后端数据格式到前端格式
      // 注意：res 可能是 { musicList: [...] } 或 { data: { musicList: [...] } }
      const musicList = res?.musicList || res?.data?.musicList || [];
      
      // artist 从用户信息获取
      const artistName = getUserData()?.name || '';
      const transformedSongs: SongItem[] = musicList.map((item: any) => ({
        id: item.musicID,
        title: item.title || '',
        artist: artistName,
        cover: item.cover || undefined,
        album: item.album || '-',
        genre: Array.isArray(item.genre) ? item.genre.map(String) : [],  // 保存原始 ID
        releaseDate: formatDateDDMMYYYY(item.publishTime),
        // musicStatus: 0-审核中, -1-未通过, 1-通过, 2-修改后审核中
        status: item.musicStatus === 1 ? 'published' 
              : item.musicStatus === -1 ? 'rejected'
              : 'review',
      }));
      setSongs(transformedSongs);
    });
  }, [genreOptions]);

  const handleDelete = (id: string) => {
    const song = songs.find(s => s.id === id);
    if (song) {
      setDeleteModal({ visible: true, id, title: song.title });
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteMusic({
        musicID: deleteModal.id,
      });
      message.success('Song deleted successfully');
      setSongs(prev => prev.filter(s => s.id !== deleteModal.id));
      setDeleteModal({ visible: false, id: '', title: '' });
    } catch (error) {
      console.error('Failed to delete song:', error);
      message.error('Failed to delete song');
      setDeleteModal({ visible: false, id: '', title: '' });
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ visible: false, id: '', title: '' });
  };

  return (
    <div className="my-songs">
      {/* Header */}
      <div className="my-songs__header">
        <div className="header-content">
          <h1 className="header-content__title">My Songs</h1>
        </div>
      </div>

      {/* Content */}
      <div className="my-songs__content">
        {songs.length > 0 ? (
          <div className="songs-list-area">
            {/* Desktop Table */}
            <div className="table-container desktop-only">
              <table className="songs-table">
                <thead>
                  <tr>
                    <th>Song</th>
                    <th>Album</th>
                    <th>Genre</th>
                    <th>Release Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {songs.map(song => (
                    <SongRow
                      key={song.id}
                      song={song}
                      onDelete={handleDelete}
                      getGenreLabel={getGenreLabel}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="mobile-cards mobile-only">
              {songs.map((song, index) => (
                <MobileSongCard
                  key={song.id}
                  song={song}
                  onDelete={handleDelete}
                  getGenreLabel={getGenreLabel}
                  isLast={index === songs.length - 1}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="my-songs-empty">
            <div className="my-songs-empty__icon">
              <img src="https://akoss.bangcdn.net/fe/MusicBig.svg" alt="" width={64} height={64} />
            </div>
            <p className="my-songs-empty__text">No Songs</p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        visible={deleteModal.visible}
        title={deleteModal.title}
        type="song"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default MySongs;
